<template>
  <b-modal
    :id="modalId"
    centered
    title="Upload Certificate"
    @hide="$emit('close')"
    hide-header-close
    ok-title="Upload Certificate"
    ok-only
    @hidden="onHidden"
    :ok-disabled="this.form.certificate == null || isBeforeTrainingEndDate"
    @ok="handleOk"
    size="md"
  >
    <div class="p-4">
      <!-- Course Info -->
      <div class="course-info-section mb-4 p-3 rounded" style="background-color: #fafafa">
        <div class="d-flex align-items-center">
          <div class="course-icon me-3">
            <i class="fe fe-book-open text-danger" style="font-size: 1.5rem"></i>
          </div>
          <div>
            <h6 class="mb-1 font-weight-bold" style="font-size: 12px">{{ data?.training?.title || "-" }}</h6>
            <small class="text-muted">
              <i class="fe fe-briefcase me-1"></i>
              {{ data?.training?.provider?.name || "-" }}
            </small>
          </div>
        </div>
      </div>

      <div v-if="isBeforeTrainingEndDate" class="alert alert-warning d-flex align-items-start" role="alert">
        <i class="fe fe-alert-triangle me-2" style="font-size: 1.1rem"></i>
        <div>Certificate can be uploaded on or after {{ formattedTrainingEndDate }}.</div>
      </div>

      <div v-if="data?.feedback" class="alert alert-danger d-flex align-items-start" role="alert">
        <i class="fe fe-edit-3 me-2" style="font-size: 1.1rem"></i>
        <div>
          <strong>Catatan Revisi:</strong>
          <div>{{ data.feedback }}</div>
        </div>
      </div>

      <!-- Upload Section -->
      <div class="upload-section mb-4">
        <label class="form-label font-weight-bold"> Certificate File <span class="text-danger">*</span> </label>
        <p class="text-muted small mb-2">
          Upload your certificate in image or PDF format (JPG, JPEG, PNG, PDF). Max file size: 2MB
        </p>

        <!-- Dropzone -->
        <div
          class="dropzone p-4 border rounded text-center"
          :class="{ 'border-danger': isDragging, 'is-invalid': !selectedFile && submitted, 'is-disabled': isBeforeTrainingEndDate }"
          @dragover.prevent="handleDragOver"
          @dragleave.prevent="handleDragLeave"
          @drop.prevent="handleDrop"
        >
          <!-- File Belum Dipilih -->
          <div v-if="!selectedFile && !previewUrl">
            <i class="fe fe-upload-cloud text-muted mb-2" style="font-size: 3rem"></i>
            <p class="text-muted mb-2">Drag & drop your certificate here or</p>
            <label class="btn btn-outline-danger btn-sm">
              Browse Files
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,application/pdf"
                :disabled="isBeforeTrainingEndDate"
                @change="handleFileSelect"
                hidden
              />
            </label>
          </div>

          <!-- File Selected / Preview -->
          <div v-else>
            <!-- Image Preview -->
            <img
              v-if="previewUrl && !isPdfFile"
              :src="previewUrl"
              alt="Certificate Preview"
              class="img-fluid rounded mb-3"
              style="max-height: 250px"
            />
            <!-- PDF Preview -->
            <div v-else-if="isPdfFile" class="pdf-preview mb-3">
              <div class="pdf-icon-container">
                <i class="fe fe-file-text"></i>
              </div>
              <p class="pdf-label mb-0">PDF File</p>
            </div>
            <div class="d-flex align-items-center justify-content-center gap-2">
              <span class="text-success">
                <i class="fe fe-check-circle me-1"></i>
                {{ selectedFile?.name }}
              </span>
              <button class="btn btn-outline-danger btn-sm" @click="removeFile">
                <i class="fe fe-x"></i>
              </button>
            </div>
            <p class="text-muted small mt-2 mb-0">Size: {{ formatFileSize(selectedFile?.size) }}</p>
          </div>
        </div>
        <small v-if="!selectedFile && submitted" class="text-danger"> Pilih sertifikat file </small>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: "UploadCertificateModal",

  props: {
    modalId: { type: String, default: "upload-certificate-modal" },
    data: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
  },

  data() {
    return {
      form: {
        certificate: null,
        id: null,
      },
      selectedFile: null,
      previewUrl: null,
      isPdfFile: false,
      note: "",
      isDragging: false,
      isUploading: false,
      submitted: false,
    };
  },

  computed: {
    trainingEndDate() {
      const schedules = (this.data?.training?.events || []).flatMap((event) => event?.schedules || []);
      const validDates = schedules.map((schedule) => schedule?.date).filter(Boolean);
      if (!validDates.length) return null;

      return validDates.reduce((latest, date) => (new Date(date) > new Date(latest) ? date : latest), validDates[0]);
    },

    isBeforeTrainingEndDate() {
      if (!this.trainingEndDate) return false;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const trainingEndDate = new Date(this.trainingEndDate);
      trainingEndDate.setHours(0, 0, 0, 0);

      return today < trainingEndDate;
    },

    formattedTrainingEndDate() {
      if (!this.trainingEndDate) return "-";

      return new Date(this.trainingEndDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
  },

  methods: {
    onHidden() {
      this.resetForm();
    },

    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleSubmit();
    },

    handleSubmit() {
      if (this.isBeforeTrainingEndDate) {
        this.$swal({
          title: "Pelatihan Belum Selesai",
          text: `Sertifikat dapat diunggah pada atau setelah ${this.formattedTrainingEndDate}.`,
          icon: "warning",
          confirmButtonText: "OK",
        });
        return;
      }

      const payload = { ...this.form };

      this.$emit("upload-certificate", payload);
    },

    handleDragOver() {
      if (this.isBeforeTrainingEndDate) return;
      this.isDragging = true;
    },

    handleDragLeave() {
      this.isDragging = false;
    },

    handleChangeFile(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.form.certificate = file;
      this.form.id = this.data.id;
    },

    resetForm() {
      this.form = { certificate: null, id: null };
    },

    handleFileSelect(event) {
      if (this.isBeforeTrainingEndDate) return;
      const file = event.target.files[0];
      this.processFile(file);
    },

    handleDrop(event) {
      this.isDragging = false;
      if (this.isBeforeTrainingEndDate) return;
      const file = event.dataTransfer.files[0];
      this.processFile(file);
    },

    processFile(file) {
      if (!file) return;

      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        this.$swal({
          title: "Tipe File Tidak Valid",
          text: "Unggah file gambar (JPG, JPEG, PNG) atau PDF",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$swal({
          title: "File Terlalu Besar",
          text: "Ukuran file maksimal 2MB",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      this.selectedFile = file;
      this.isPdfFile = file.type === "application/pdf";

      this.form.certificate = file;
      this.form.id = this.data.id;

      if (!this.isPdfFile) {
        this.previewUrl = URL.createObjectURL(file);
      } else {
        this.previewUrl = null;
      }
    },

    removeFile() {
      this.selectedFile = null;
      this.isPdfFile = false;
      this.form.certificate = null;

      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },
  },

  beforeDestroy() {
    // Clean up object URL
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  },
};
</script>

<style scoped>
.dropzone {
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6 !important;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dropzone:hover,
.dropzone.border-danger {
  border-color: #e63757 !important;
  background-color: #fff5f7;
}

.dropzone.is-invalid {
  border-color: #dc3545 !important;
}

.dropzone.is-disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.course-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #fce4e4;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gap-2 {
  gap: 0.5rem;
}

.me-1 {
  margin-right: 0.25rem;
}

.me-2 {
  margin-right: 0.5rem;
}

.me-3 {
  margin-right: 1rem;
}

/* PDF Preview Styles */
.pdf-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.pdf-preview .pdf-icon-container {
  width: 80px;
  height: 100px;
  background: linear-gradient(135deg, #e63757 0%, #c82333 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

.pdf-preview .pdf-icon-container i {
  font-size: 2.5rem;
  color: #fff;
}

.pdf-preview .pdf-label {
  margin-top: 1rem;
  font-weight: 600;
  color: #1a202c;
}
</style>
